// Presentational parts of the P7-A billing and credit screens (BILLING-02/03/04/06/07). Props in,
// markup out: no queries, so they render in Node tests (react-dom/server) and on any page.
// Truthful states only: anything that needs the owner's gateway shows "pending activation", never a
// working button (DESIGN_SYSTEM §9.1: a pending feature is never labelled live).
import type { ReactNode } from "react";
import { formatDate, formatDateTime, formatMoney } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import { Badge, Banner, Button, DataTable, type DataState } from "@ady/ui";
import type {
  AddonsView,
  BillingSummary7,
  CreditsView,
  Invoice,
  LedgerEntry,
  WalletView,
} from "./api";

export const BILLING_TID = {
  credit: "billing-credit",
  creditLow: "billing-credit-low",
  gatewayPending: "billing-gateway-pending",
  ledger: "wallet-ledger",
  ledgerMore: "wallet-ledger-more",
  trial: "billing-trial",
  trialStart: "billing-trial-start",
  invoices: "billing-invoices",
  addons: "billing-addons",
  refunds: "billing-refunds",
  walletPage: "wallet-page",
} as const;

export const entryLabel = (kind: string) =>
  i18n.tDynamic(`wallet.statement.kinds.${kind}`, "wallet.statement.kinds.unknown");
export const refLabel = (refType: string) =>
  i18n.tDynamic(`wallet.statement.refs.${refType}`, "wallet.statement.refs.unknown");
export const offerLabel = (key: string) =>
  i18n.tDynamic(`wallet.addons.offers.${key}`, "wallet.addons.offers.unknown");
export const invoiceStateLabel = (state: string) =>
  i18n.tDynamic(`wallet.invoices.states.${state}`, "wallet.invoices.states.unknown");

/** Signed balance effect for display: "+Rs 500", "−Rs 120", or the held amount for holds. */
export function entryAmountText(
  e: Pick<LedgerEntry, "kind" | "amount_paisa" | "balance_change_paisa">,
): string {
  if (e.balance_change_paisa > 0) return `+${formatMoney(e.balance_change_paisa)}`;
  if (e.balance_change_paisa < 0) return formatMoney(e.balance_change_paisa);
  return formatMoney(e.amount_paisa);
}

export function GatewayPendingBanner({ testId = BILLING_TID.gatewayPending }: { testId?: string }) {
  return (
    <Banner
      kind="pending-activation"
      title={t("wallet.gateway.title")}
      steps={[{ label: t("wallet.gateway.step"), actor: "platform", done: false }]}
      data-testid={testId}
    >
      {t("wallet.gateway.body")}
    </Banner>
  );
}

export function CreditSummary({
  view,
  statementLink,
}: {
  view: WalletView;
  statementLink?: ReactNode;
}) {
  const w = view.wallet;
  return (
    <div className="flex flex-col gap-4" data-testid={BILLING_TID.credit}>
      <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-3">
        {(
          [
            ["wallet.credit.balance", w.balance_paisa, "balance"],
            ["wallet.credit.reserved", w.reserved_paisa, "reserved"],
            ["wallet.credit.available", w.available_paisa, "available"],
          ] as const
        ).map(([label, paisa, key]) => (
          <div key={key} className="flex flex-col gap-0.5">
            <dt className="text-sm text-fg-secondary">{t(label)}</dt>
            <dd className="ady-ledger text-title-sm font-semibold" data-amount={key}>
              {formatMoney(paisa)}
            </dd>
          </div>
        ))}
      </dl>
      {w.low && w.low_threshold_paisa !== null ? (
        <p
          className="flex flex-wrap items-center gap-2 text-sm"
          data-testid={BILLING_TID.creditLow}
        >
          <Badge tone="warning">{t("wallet.credit.low")}</Badge>
          {t("wallet.credit.lowHelp", { threshold: formatMoney(w.low_threshold_paisa) })}
        </p>
      ) : null}
      {view.topup.available ? null : <GatewayPendingBanner />}
      {statementLink}
    </div>
  );
}

export function LedgerTable({
  entries,
  state,
  onRetry,
  hasMore,
  loadingMore,
  onMore,
}: {
  entries: LedgerEntry[];
  state: DataState;
  onRetry?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onMore?: () => void;
}) {
  const what = (e: LedgerEntry) => (
    <span className="flex flex-col">
      <span>{entryLabel(e.kind)}</span>
      <span className="text-sm text-fg-secondary">
        {e.op === "sweep" ? t("wallet.statement.swept") : refLabel(e.ref_type)}
      </span>
      {e.kind === "debit" && e.requested_paisa !== null && e.requested_paisa > e.amount_paisa ? (
        <span className="text-sm text-fg-secondary">
          {t("wallet.statement.uncollected", {
            amount: formatMoney(e.requested_paisa - e.amount_paisa),
          })}
        </span>
      ) : null}
    </span>
  );
  return (
    <div className="flex flex-col gap-3">
      <DataTable
        caption={t("wallet.statement.caption")}
        data-testid={BILLING_TID.ledger}
        rows={entries}
        rowId={(e) => e.id}
        state={state === "ready" && entries.length === 0 ? "empty" : state}
        onRetry={onRetry}
        empty={{ title: t("wallet.statement.empty"), body: t("wallet.statement.emptyBody") }}
        columns={[
          {
            id: "when",
            header: t("wallet.statement.when"),
            cell: (e) => formatDateTime(e.created_at),
          },
          { id: "what", header: t("wallet.statement.what"), cell: what },
          {
            id: "amount",
            header: t("wallet.statement.amount"),
            numeric: true,
            align: "end",
            cell: entryAmountText,
          },
          {
            id: "after",
            header: t("wallet.statement.after"),
            numeric: true,
            align: "end",
            priority: 3,
            cell: (e) => formatMoney(e.available_after_paisa),
          },
        ]}
        card={{
          title: (e) => entryLabel(e.kind),
          amount: entryAmountText,
          details: (e) => `${formatDateTime(e.created_at)} · ${refLabel(e.ref_type)}`,
        }}
      />
      {hasMore ? (
        <div>
          <Button
            icon="chevronDown"
            variant="secondary"
            loading={loadingMore}
            onClick={onMore}
            data-testid={BILLING_TID.ledgerMore}
          >
            {t("wallet.statement.more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function TrialPanel({
  trial,
  isOwner,
  busy,
  onStart,
}: {
  trial: NonNullable<BillingSummary7["trial"]>;
  isOwner: boolean;
  busy?: boolean;
  onStart?: () => void;
}) {
  const plan = trial.plans[0];
  const current = trial.current;
  let body: ReactNode;
  if (current) {
    const planName = trial.plans.find((p) => p.id === current.plan.id)?.name ?? current.plan.key;
    const when = current.ends_at ? formatDate(current.ends_at, { style: "medium" }) : "";
    body = (
      <p className="text-md" data-state={current.state}>
        {current.state === "trial"
          ? t("wallet.trial.active", { plan: planName, when })
          : t("wallet.trial.ended", { plan: planName, when })}
      </p>
    );
  } else if (trial.available && plan) {
    body = (
      <div className="flex flex-col gap-3">
        <p className="text-md">
          {t("wallet.trial.offer", { plan: plan.name, days: plan.trial_days })}
        </p>
        {isOwner ? (
          <div>
            <Button
              icon="rocket"
              loading={busy}
              loadingLabel={t("wallet.trial.starting")}
              onClick={onStart}
              data-testid={BILLING_TID.trialStart}
            >
              {t("wallet.trial.start")}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-fg-secondary">{t("wallet.trial.ownerOnly")}</p>
        )}
      </div>
    );
  } else {
    const reason =
      trial.reason === "trial_used"
        ? t("wallet.trial.used")
        : trial.reason === "already_subscribed"
          ? t("wallet.trial.subscribed")
          : t("wallet.trial.noPlan");
    body = <p className="text-md text-fg-secondary">{reason}</p>;
  }
  return (
    <div
      className="flex flex-col gap-2"
      data-testid={BILLING_TID.trial}
      data-reason={trial.reason ?? "available"}
    >
      {body}
      <p className="text-sm text-fg-secondary">{t("wallet.trial.noConvert")}</p>
    </div>
  );
}

export function InvoicesTable({
  invoices,
  state,
  onRetry,
  onView,
}: {
  invoices: Invoice[];
  state: DataState;
  onRetry?: () => void;
  /** D-067: open one invoice to read or print. */
  onView?: (invoiceId: string) => void;
}) {
  return (
    <DataTable
      caption={t("wallet.invoices.caption")}
      data-testid={BILLING_TID.invoices}
      rows={invoices}
      rowId={(i) => i.id}
      state={state === "ready" && invoices.length === 0 ? "empty" : state}
      onRetry={onRetry}
      empty={{ title: t("wallet.invoices.empty"), body: t("wallet.invoices.emptyBody") }}
      columns={[
        { id: "number", header: t("wallet.invoices.number"), cell: (i) => i.number },
        {
          id: "date",
          header: t("wallet.invoices.date"),
          cell: (i) => formatDate(i.issued_at, { style: "medium" }),
        },
        {
          id: "amount",
          header: t("wallet.invoices.amount"),
          numeric: true,
          align: "end",
          cell: (i) => formatMoney(i.amount_paisa),
        },
        {
          id: "status",
          header: t("wallet.invoices.status"),
          cell: (i) => (
            <Badge
              tone={i.state === "paid" ? "success" : i.state === "open" ? "warning" : "neutral"}
            >
              {invoiceStateLabel(i.state)}
            </Badge>
          ),
        },
        ...(onView
          ? [
              {
                id: "view",
                header: "",
                align: "end" as const,
                cell: (i: Invoice) => (
                  <button
                    type="button"
                    className="text-xs font-semibold text-link"
                    onClick={() => onView(i.id)}
                  >
                    {t("billingOps.shop.invoiceView")}
                  </button>
                ),
              },
            ]
          : []),
      ]}
      card={{
        title: (i) => i.number,
        amount: (i) => formatMoney(i.amount_paisa),
        status: (i) => invoiceStateLabel(i.state),
        details: (i) => formatDate(i.issued_at, { style: "medium" }),
      }}
    />
  );
}

export function AddonsPanel({
  view,
  quotaName,
}: {
  view: AddonsView;
  quotaName: (key: string) => string;
}) {
  return (
    <div className="flex flex-col gap-4" data-testid={BILLING_TID.addons}>
      <ul className="flex flex-col gap-2">
        {view.offers.map((o) => (
          <li
            key={o.key}
            className="flex flex-wrap items-center justify-between gap-2"
            data-offer={o.key}
          >
            <span className="flex flex-col">
              <span className="font-medium">{offerLabel(o.key)}</span>
              <span className="text-sm text-fg-secondary">
                {t("wallet.addons.increment", { n: o.increment, quota: quotaName(o.quota_key) })}
              </span>
            </span>
            <span className="text-sm text-fg-secondary">
              {o.price ? null : t("wallet.addons.priceNotSet")}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">{t("wallet.addons.active")}</h3>
        {view.active.length === 0 ? (
          <p className="text-sm text-fg-secondary">{t("wallet.addons.none")}</p>
        ) : (
          <ul className="text-sm">
            {view.active.map((a) => (
              <li key={a.id}>
                {t("wallet.addons.increment", { n: a.quantity, quota: quotaName(a.quota_key) })}
              </li>
            ))}
          </ul>
        )}
      </div>
      {view.purchase.available ? null : (
        <GatewayPendingBanner testId={`${BILLING_TID.gatewayPending}-addons`} />
      )}
    </div>
  );
}

export function RefundPolicy({ policy }: { policy: CreditsView["refund_policy"] }) {
  return (
    <p className="text-md" data-testid={BILLING_TID.refunds}>
      {policy.window_days === null
        ? t("wallet.refunds.none")
        : t("wallet.refunds.window", { days: policy.window_days })}
    </p>
  );
}
